"use client"

import type React from "react"
import { useState, useRef } from "react"
import { Button } from "../components/ui/button"
import { ArrowUpIcon, SearchIcon } from "lucide-react"
import { generateGeminiContent } from "../services/usersapi"
import { useToast } from "../components/ui/use-toast"
import { useNavigate } from "react-router-dom"

const AIChat: React.FC = () => {
  const [inputValue, setInputValue] = useState("")
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()
  const navigate = useNavigate()

  const handleInputFocus = () => {
    setIsInputFocused(true)
  }

  const handleInputBlur = () => {
    if (!inputValue) {
      setIsInputFocused(false)
    }
  }

  const handleSubmit = async () => {
    if (inputValue.trim() && !isLoading) {
      setIsLoading(true)
      try {
        const result = await generateGeminiContent(inputValue.trim())

        // Navigate to results page with the query and response
        navigate("/result", {
          state: {
            query: inputValue.trim(),
            response: result,
          },
        })

        toast({
          title: "Response generated successfully!",
          description: "Redirecting to results page...",
        })
      } catch (error) {
        console.error("Error generating content:", error)
        toast({
          title: "Error",
          description: "Failed to generate response. Please try again.",
          variant: "destructive",
        })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading) {
      handleSubmit()
    }
  }

  const recentSearches = [
    "What are key strategies to attract top talents?",
    "How can I improve candidate experience during hiring?",
    "What are current trends in sourcing diverse candidates?",
  ]

  const handleRecentSearchClick = (search: string) => {
    if (!isLoading) {
      setInputValue(search)
      setIsInputFocused(true)
      inputRef.current?.focus()
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-4xl mx-auto text-center">
        {/* Main Title */}
        <h1 className="text-5xl md:text-6xl font-semibold text-foreground mb-4 tracking-tight">
          Meet <span className="text-orange-600">EarlyJobs</span> AI Mode
        </h1>

        {/* Subtitle */}
        <p className="text-xl md:text-2xl mb-12" style={{ color: "var(--ai-text-muted)" }}>
          Ask detailed questions for better responses
        </p>

        {/* Input Container */}
        <div className="relative max-w-3xl mx-auto">
          {/* Gradient Border Wrapper */}
          <div
            className={`relative transition-all duration-300 ${
              !isInputFocused && !inputValue
                ? "bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 p-0.5 rounded-3xl"
                : ""
            }`}
          >
            {/* Inner Container */}
            <div
              className={`relative transition-all duration-300 ${
                !isInputFocused && !inputValue ? "bg-background rounded-3xl" : ""
              }`}
            >
              <div
                className={`flex items-center gap-3 px-6 py-5 rounded-3xl transition-all duration-300 ${
                  !isInputFocused && !inputValue ? "bg-transparent" : "bg-background border border-input"
                }`}
              >
                {/* Input Field */}
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyPress={handleKeyPress}
                  placeholder={isLoading ? "Generating response..." : "Ask anything"}
                  disabled={isLoading}
                  className="flex-1 border-none outline-none text-foreground text-lg py-2 bg-transparent disabled:opacity-50 ai-placeholder"
                />

                {/* Submit Button */}
                <Button
                  onClick={handleSubmit}
                  size="sm"
                  className="p-2 h-auto rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: "orange-600",
                  }}
                  disabled={!inputValue.trim() || isLoading}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ArrowUpIcon className="w-5 h-5 text-white" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Recent Searches */}
          <div className="mt-8 space-y-4">
            {recentSearches.map((search, index) => (
              <button
                key={index}
                onClick={() => handleRecentSearchClick(search)}
                disabled={isLoading}
                className="flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl hover:bg-muted transition-colors duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SearchIcon
                  className="w-4 h-4 transition-colors group-hover:text-[var(--ai-purple)]"
                  style={{
                    color: "var(--ai-text-muted)",
                  }}
                />
                <span className="text-foreground text-base">{search}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AIChat

